/**
 * Product Media Gallery - Carousel & Viewer functionality
 * Uses shared MediaViewer for fullscreen viewing
 */
(function() {
  var galleryData = new Map();
  
  // ===== Media Viewer Integration =====
  
  function initViewer(gallery) {
    var viewer = gallery.querySelector('.media-viewer');
    if (!viewer) return;

    var mediaItems = gallery.querySelectorAll('[data-media-id][data-media-type]');

    // Build the viewer's media list at open time from the media that is currently
    // VISIBLE (i.e. the selected colour only), deduped by media-id. This keeps the
    // fullscreen viewer in sync with the on-page colour filtering.
    function buildVisibleMedia() {
      var seenIds = new Set();
      var allMedia = [];
      var indexById = {};
      mediaItems.forEach(function(item) {
        if (item.closest('.pmg-hidden') || item.classList.contains('pmg-hidden')) return;
        var mediaId = item.getAttribute('data-media-id');
        if (seenIds.has(mediaId)) return;
        seenIds.add(mediaId);

        var type = item.getAttribute('data-media-type');
        var img = item.querySelector('img');
        var video = item.querySelector('video');

        var mediaData = {
          id: mediaId,
          type: type,
          imageSrc: img ? (img.getAttribute('data-lightbox-src') || img.currentSrc || img.src) : null,
          alt: img ? img.alt : '',
          videoSrc: null,
          poster: null
        };

        if (video) {
          var source = video.querySelector('source');
          mediaData.videoSrc = source ? source.src : video.src;
          mediaData.poster = video.poster || '';
        }

        allMedia.push(mediaData);
        indexById[mediaId] = allMedia.length - 1;
      });
      return { list: allMedia, indexById: indexById };
    }

    function openViewer(mediaId) {
      if (typeof window.MediaViewer === 'undefined') return;
      var built = buildVisibleMedia();
      if (!built.list.length) return;
      var startIndex = built.indexById[mediaId] !== undefined ? built.indexById[mediaId] : 0;
      window.MediaViewer.open({
        viewer: viewer,
        mediaItems: built.list,
        startIndex: startIndex
      });
    }

    // Event listeners for ALL media items (including duplicates from mobile/desktop)
    mediaItems.forEach(function(item) {
      var mediaId = item.getAttribute('data-media-id');
      var type = item.getAttribute('data-media-type');

      // For videos, the play button should open the viewer
      var playBtn = item.querySelector('[data-play-btn]');
      if (playBtn) {
        playBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          openViewer(mediaId);
        });
      }

      // For images, clicking anywhere opens the viewer
      if (type === 'image') {
        item.addEventListener('click', function() {
          openViewer(mediaId);
        });
      }

      // On mobile, clicking video wrapper also opens viewer
      item.addEventListener('click', function() {
        if (window.innerWidth < 990 && (type === 'video' || type === 'external_video')) {
          openViewer(mediaId);
        }
      });
    });
  }
  
  // ===== Carousel Functionality =====
  
  function initGalleryCarousels() {
    var galleries = document.querySelectorAll('.product-media-gallery[data-enable-carousel="true"]');
    
    galleries.forEach(function(gallery) {
      var mediaCount = parseInt(gallery.getAttribute('data-media-count'), 10);
      if (mediaCount <= 1) return;
      
      var carouselContainer = gallery.querySelector('[data-gallery-carousel]');
      if (!carouselContainer) return;
      
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            startCarousel(gallery, carouselContainer);
          } else {
            stopCarousel(gallery, carouselContainer);
          }
        });
      }, { threshold: 0.5 });
      
      observer.observe(carouselContainer);
    });
  }
  
  function startCarousel(gallery, container) {
    var mediaItems = container.querySelectorAll('.product-media-gallery__carousel-item');
    var pill = container.querySelector('.product-media-gallery__progress-pill');
    var mediaCount = parseInt(gallery.getAttribute('data-media-count'), 10);
    var imageDuration = (parseInt(gallery.getAttribute('data-carousel-duration'), 10) || 3) * 1000;
    
    if (mediaCount <= 1) return;
    if (galleryData.has(container)) return;
    
    var currentIndex = 0;
    var timeoutId = null;
    var videoEndedHandler = null;
    var isPaused = false;
    var resumeTimeoutId = null;
    
    function setPillDuration(durationMs) {
      if (pill) pill.style.setProperty('--pill-duration', (durationMs / 1000) + 's');
    }
    
    function restartPillAnimation() {
      if (pill) {
        pill.classList.remove('is-filling');
        void pill.offsetWidth;
        pill.classList.add('is-filling');
      }
    }
    
    function pausePillAnimation() {
      if (pill) pill.classList.add('is-paused');
    }
    
    function resumePillAnimation() {
      if (pill) pill.classList.remove('is-paused');
    }
    
    function isItemHidden(item) {
      return item && item.classList.contains('pmg-hidden');
    }

    // Next visible slide, skipping media hidden by colour filtering. Falls back to
    // the current index when nothing else is visible (single-image colour).
    function nextVisibleIndex(from) {
      var n = mediaItems.length;
      for (var step = 1; step <= n; step++) {
        var idx = (from + step) % n;
        if (!isItemHidden(mediaItems[idx])) return idx;
      }
      return from;
    }

    function nextSlide() {
      if (isPaused) return;
      showSlide(nextVisibleIndex(currentIndex), false);
    }
    
    function pauseCarousel() {
      isPaused = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      pausePillAnimation();
      
      var currentItem = mediaItems[currentIndex];
      var video = currentItem ? currentItem.querySelector('video') : null;
      if (video) video.pause();
    }
    
    function resumeCarousel() {
      isPaused = false;
      resumePillAnimation();
      
      var currentItem = mediaItems[currentIndex];
      var video = currentItem ? currentItem.querySelector('video') : null;
      if (video) {
        video.play().catch(function() {});
      } else {
        scheduleNextSlide();
      }
    }
    
    function scheduleNextSlide() {
      if (isPaused) return;
      
      var currentItem = mediaItems[currentIndex];
      var video = currentItem ? currentItem.querySelector('video') : null;
      var isVideo = video !== null;
      
      if (videoEndedHandler) {
        mediaItems.forEach(function(item) {
          var v = item.querySelector('video');
          if (v) v.removeEventListener('ended', videoEndedHandler);
        });
      }
      
      if (isVideo) {
        videoEndedHandler = function() {
          if (!isPaused) nextSlide();
        };
        video.addEventListener('ended', videoEndedHandler, { once: true });
        
        if (video.duration && !isNaN(video.duration)) {
          setPillDuration(video.duration * 1000);
          restartPillAnimation();
        } else {
          video.addEventListener('loadedmetadata', function() {
            if (video.duration && !isNaN(video.duration)) {
              setPillDuration(video.duration * 1000);
              restartPillAnimation();
            }
          }, { once: true });
          setPillDuration(imageDuration);
          restartPillAnimation();
        }
      } else {
        setPillDuration(imageDuration);
        restartPillAnimation();
        timeoutId = setTimeout(nextSlide, imageDuration);
      }
    }
    
    function showSlide(newIndex, isUserInteraction) {
      var prevIndex = currentIndex;
      currentIndex = newIndex;
      
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      mediaItems.forEach(function(item, i) {
        var video = item.querySelector('video');
        
        if (i === prevIndex && i !== newIndex) {
          item.classList.add('is-leaving');
          item.classList.remove('is-active');
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        } else if (i === newIndex) {
          item.classList.remove('is-leaving');
          item.classList.add('is-active');
          if (video && !isPaused) {
            video.currentTime = 0;
            video.play().catch(function() {});
          }
        } else {
          item.classList.remove('is-active', 'is-leaving');
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        }
      });
      
      setTimeout(function() {
        mediaItems.forEach(function(item) {
          item.classList.remove('is-leaving');
        });
      }, 600);
      
      var thumbnails = gallery.querySelectorAll('.product-media-gallery__thumbnail');
      thumbnails.forEach(function(thumb, i) {
        thumb.classList.toggle('is-active', i === newIndex);
      });
      
      if (!isPaused) scheduleNextSlide();
    }
    
    function handleUserInteraction() {
      pauseCarousel();
      if (resumeTimeoutId) clearTimeout(resumeTimeoutId);
      resumeTimeoutId = setTimeout(function() {
        resumeCarousel();
      }, 5000);
    }
    
    container.addEventListener('mouseenter', pauseCarousel);
    container.addEventListener('mouseleave', function() {
      if (resumeTimeoutId) clearTimeout(resumeTimeoutId);
      resumeCarousel();
    });
    container.addEventListener('touchstart', handleUserInteraction, { passive: true });
    container.addEventListener('click', handleUserInteraction);
    
    // Start on the first visible slide (colour filtering may hide the very first).
    var startIndex = 0;
    for (var si = 0; si < mediaItems.length; si++) {
      if (!isItemHidden(mediaItems[si])) { startIndex = si; break; }
    }
    currentIndex = startIndex;

    mediaItems.forEach(function(item, i) {
      item.classList.toggle('is-active', i === startIndex);
    });

    var firstItem = mediaItems[startIndex];
    var firstVideo = firstItem ? firstItem.querySelector('video') : null;

    if (firstVideo) {
      firstVideo.currentTime = 0;
      firstVideo.play().catch(function() {});
    }

    scheduleNextSlide();
    
    galleryData.set(container, {
      currentIndex: currentIndex,
      showSlide: showSlide,
      // Drive the carousel to a specific slide as if the user interacted:
      // pauses autoplay briefly so it won't immediately override the choice.
      goTo: function(i) {
        if (typeof i !== 'number' || isNaN(i)) return;
        handleUserInteraction();
        showSlide(i, true);
      },
      cleanup: function() {
        if (timeoutId) clearTimeout(timeoutId);
        if (resumeTimeoutId) clearTimeout(resumeTimeoutId);
        if (videoEndedHandler) {
          mediaItems.forEach(function(item) {
            var video = item.querySelector('video');
            if (video) video.removeEventListener('ended', videoEndedHandler);
          });
        }
        container.removeEventListener('mouseenter', pauseCarousel);
        container.removeEventListener('mouseleave', resumeCarousel);
      }
    });
    
    var thumbnails = gallery.querySelectorAll('.product-media-gallery__thumbnail');
    thumbnails.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        var index = parseInt(thumb.getAttribute('data-index'), 10);
        if (!isNaN(index)) {
          handleUserInteraction();
          showSlide(index, true);
        }
      });
    });
  }
  
  function stopCarousel(gallery, container) {
    var data = galleryData.get(container);
    if (data) {
      if (data.cleanup) data.cleanup();
      galleryData.delete(container);
    }
    
    var mediaItems = container.querySelectorAll('.product-media-gallery__carousel-item');
    var pill = container.querySelector('.product-media-gallery__progress-pill');
    
    mediaItems.forEach(function(item, i) {
      var video = item.querySelector('video');
      item.classList.remove('is-leaving');
      item.classList.toggle('is-active', i === 0);
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    
    var thumbnails = gallery.querySelectorAll('.product-media-gallery__thumbnail');
    thumbnails.forEach(function(thumb, i) {
      thumb.classList.toggle('is-active', i === 0);
    });
    
    if (pill) {
      pill.classList.remove('is-filling');
      pill.style.removeProperty('--pill-duration');
    }
  }
  
  // ===== Variant Media Sync =====
  // When a variant is selected, ProductVariantSelector dispatches
  // 'product:media-change' with the variant's featured media id. Drive every
  // gallery to that media: the mobile carousel AND the desktop main image,
  // across whichever layout (collage / thumbnail / single) is active.

  function getMediaImageData(gallery, mediaId) {
    // The mobile carousel/collage items are always rendered (just CSS-hidden on
    // desktop) and carry every media at high resolution, so they are a reliable
    // source for the image URL regardless of the active desktop layout.
    var el = gallery.querySelector('.product-media-gallery__carousel-item[data-media-id="' + mediaId + '"]')
          || gallery.querySelector('.product-media-gallery__mobile-collage-main[data-media-id="' + mediaId + '"]')
          || gallery.querySelector('.product-media-gallery__mobile-collage-item[data-media-id="' + mediaId + '"]')
          || gallery.querySelector('[data-media-id="' + mediaId + '"]');
    if (!el) return null;

    var type = el.getAttribute('data-media-type');
    if (type === 'video' || type === 'external_video' || type === 'model') return null;

    var img = el.querySelector('img');
    if (!img) return null;

    var raw = img.getAttribute('src') || '';
    function w(n) { return raw.replace(/width=\d+/, 'width=' + n); }

    return {
      src: w(1200),
      srcset: w(600) + ' 600w, ' + w(900) + ' 900w, ' + w(1200) + ' 1200w, ' + w(1800) + ' 1800w',
      lightbox: img.getAttribute('data-lightbox-src') || w(2400),
      alt: img.getAttribute('alt') || '',
      index: el.getAttribute('data-index')
    };
  }

  function fadeSwapImage(wrap, data, mediaId) {
    var img = wrap.querySelector('img');
    if (!img) return;

    img.style.transition = 'opacity .2s ease';
    img.style.opacity = '0';

    requestAnimationFrame(function() {
      setTimeout(function() {
        img.src = data.src;
        if (data.srcset) img.srcset = data.srcset;
        img.alt = data.alt;
        if (data.lightbox) img.setAttribute('data-lightbox-src', data.lightbox);
        wrap.setAttribute('data-media-id', mediaId);
        if (data.index != null) wrap.setAttribute('data-index', data.index);

        var reveal = function() { img.style.opacity = '1'; };
        if (img.complete) {
          reveal();
        } else {
          img.addEventListener('load', reveal, { once: true });
          img.addEventListener('error', reveal, { once: true });
        }
      }, 120);
    });
  }

  function setActiveCarouselItem(container, index) {
    container.querySelectorAll('.product-media-gallery__carousel-item').forEach(function(it, i) {
      it.classList.remove('is-leaving');
      it.classList.toggle('is-active', i === index);
    });
  }

  // The distinct colour groups (filename prefixes) that correspond to a variant
  // colour, as computed server-side. Media whose group is NOT in this set (e.g.
  // shared lifestyle shots or videos) is always shown, never filtered out.
  function getColorGroupSet(gallery) {
    var raw = gallery.getAttribute('data-color-groups') || '';
    var set = Object.create(null);
    raw.split(',').forEach(function(g) {
      g = g.trim();
      if (g) set[g] = true;
    });
    return set;
  }

  function groupOfMedia(gallery, mediaId) {
    var el = gallery.querySelector('[data-media-id="' + mediaId + '"][data-media-group]');
    return el ? (el.getAttribute('data-media-group') || '') : '';
  }

  function firstMediaIdOfGroup(gallery, group) {
    var els = gallery.querySelectorAll('[data-media-id][data-media-group]');
    for (var i = 0; i < els.length; i++) {
      if ((els[i].getAttribute('data-media-group') || '') === group) {
        return els[i].getAttribute('data-media-id');
      }
    }
    return null;
  }

  // Show only the media belonging to the selected colour. `mainMediaId` is the
  // photo shown large in a layout's dedicated main slot; its duplicate in the grid
  // (if present) is hidden so the same photo never appears twice.
  function filterGalleryToGroup(gallery, selectedGroup, mainMediaId) {
    if (gallery.getAttribute('data-color-filter') !== 'true') return;
    if (!selectedGroup) return;
    var colorGroups = getColorGroupSet(gallery);

    // Grid-style items sit next to a separate main slot -> also de-dupe the main.
    gallery.querySelectorAll(
      '.product-media-gallery__grid-item, .product-media-gallery__mobile-collage-item'
    ).forEach(function(item) {
      var g = item.getAttribute('data-media-group') || '';
      var offColor = g && colorGroups[g] && g !== selectedGroup;
      var isMainDup = mainMediaId && item.getAttribute('data-media-id') === mainMediaId;
      item.classList.toggle('pmg-hidden', !!(offColor || isMainDup));
    });

    // Stacked items, thumbnail strips and carousel slides: colour filter only.
    gallery.querySelectorAll(
      '.product-media-gallery__stacked-item, .product-media-gallery__thumbnail, .product-media-gallery__carousel-item'
    ).forEach(function(item) {
      var g = item.getAttribute('data-media-group') || '';
      var offColor = g && colorGroups[g] && g !== selectedGroup;
      item.classList.toggle('pmg-hidden', !!offColor);
    });
  }

  function syncGalleryToMedia(gallery, mediaId) {
    // Colour filter first, de-duping the image about to become the main slot.
    var selectedGroup = groupOfMedia(gallery, mediaId);
    filterGalleryToGroup(gallery, selectedGroup, mediaId);

    var ref = gallery.querySelector('[data-media-id="' + mediaId + '"][data-index]');
    var index = ref ? parseInt(ref.getAttribute('data-index'), 10) : NaN;

    // 1) Mobile carousel — drive the running carousel, or set the active slide
    //    directly if it hasn't been initialised (e.g. currently off-screen).
    var carouselContainer = gallery.querySelector('[data-gallery-carousel]');
    if (carouselContainer && !isNaN(index)) {
      var data = galleryData.get(carouselContainer);
      if (data && typeof data.goTo === 'function') {
        data.goTo(index);
      } else {
        setActiveCarouselItem(carouselContainer, index);
      }
    }

    // 2) Main image slot(s) — desktop collage (__main), thumbnail (__featured),
    //    single (__single) and the mobile collage main.
    var mainWraps = [
      gallery.querySelector('.product-media-gallery__main')
        || gallery.querySelector('.product-media-gallery__featured')
        || gallery.querySelector('.product-media-gallery__single'),
      gallery.querySelector('.product-media-gallery__mobile-collage-main')
    ];
    mainWraps.forEach(function(mainWrap) {
      if (mainWrap && mainWrap.getAttribute('data-media-id') !== mediaId) {
        var imgData = getMediaImageData(gallery, mediaId);
        if (imgData) fadeSwapImage(mainWrap, imgData, mediaId);
      }
    });

    // 3) Keep thumbnail strips (desktop + mobile) highlighting the right media.
    gallery.querySelectorAll('.product-media-gallery__thumbnail').forEach(function(thumb) {
      thumb.classList.toggle('is-active', thumb.getAttribute('data-media-id') === mediaId);
    });
  }

  function initVariantMediaSync() {
    document.addEventListener('product:media-change', function(e) {
      var mediaId = (e.detail && e.detail.mediaId != null) ? String(e.detail.mediaId) : '';
      if (!mediaId) return;
      document.querySelectorAll('.product-media-gallery').forEach(function(gallery) {
        syncGalleryToMedia(gallery, mediaId);
      });
    });
  }

  // On load, collapse each gallery to its selected colour so only that colour's
  // images show from the start (no need to wait for a variant click).
  function initColorFilter() {
    document.querySelectorAll('.product-media-gallery[data-color-filter="true"]').forEach(function(gallery) {
      var selectedGroup = gallery.getAttribute('data-selected-group') || '';
      if (!selectedGroup) return;

      var mainSlot = gallery.querySelector('.product-media-gallery__main')
                  || gallery.querySelector('.product-media-gallery__featured')
                  || gallery.querySelector('.product-media-gallery__single');
      var mainId = mainSlot ? mainSlot.getAttribute('data-media-id') : null;
      var mainGroup = mainId ? groupOfMedia(gallery, mainId) : '';

      if (mainGroup && mainGroup === selectedGroup) {
        // Common case: the pre-rendered main already shows the selected colour.
        filterGalleryToGroup(gallery, selectedGroup, mainId);
      } else {
        // Deep-linked to a non-default colour (?variant=…): point the gallery at
        // that colour's first image and filter around it.
        var targetId = firstMediaIdOfGroup(gallery, selectedGroup) || mainId;
        if (targetId) syncGalleryToMedia(gallery, targetId);
      }
    });
  }

  function initAllGalleries() {
    var allGalleries = document.querySelectorAll('.product-media-gallery');
    allGalleries.forEach(function(gallery) {
      initViewer(gallery);
    });
    initGalleryCarousels();
    initVariantMediaSync();
    initColorFilter();
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries);
  } else {
    initAllGalleries();
  }
})();
